import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import CheckableInput from './CheckableInput';
import ButtonCheckable from './ButtonCheckable';
import useCheckable from './hooks/useCheckable';
import { useCombinedRefs, buildRegisterProps, fieldHasErrors, isInputChecked, callAll } from '../../../helpers';
import { Helpblock, Label } from '../../utility';
import { CheckableProps } from '../types';
import { FormControlFeedback } from '../form-control';
import { Tooltip } from '../../tooltip';

/**
 * This is the underlying implementation for `<Checkbox>` and `<Radio>`.
 */
export default function Checkable(props: CheckableProps): JSX.Element {
	const { getProps, isGroup, checkGroup } = useCheckable(props);
	const { className, inputonly, id, dirty, validators, inline, label, onChange, onBlur, tooltip, indeterminate, ...checkableProps } = getProps();
	const formCheckClasses = [className, inline && 'form-check-inline', 'form-check'].filter(Boolean).join(' ');

	const { formState, register } = useFormContext() || {};

	const innerRef = useRef();
	const registerProps = buildRegisterProps(register, {
		name: checkableProps.name,
		required: checkableProps.required,
		validators
	});
	const combinedRef = useCombinedRefs(registerProps.ref, innerRef);

	const [isInvalid, setIsInvalid] = useState(!!(dirty && checkableProps.required));
	const formCheckLabelClasses = ['form-check-label', isInvalid && 'is-invalid'].filter(Boolean).join(' ');
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsInvalid(checkableProps.required && !isInputChecked(event.target));
		callAll(registerProps.onChange, onChange, checkGroup.onChange)(event);
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		callAll(registerProps.onBlur, onBlur)(event);
	};

	const firstUpdate = useRef(!!dirty);
	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}

		setIsInvalid(fieldHasErrors(checkableProps.name, formState?.errors));
	}, [formState, checkableProps.name]);

	const helptextId = checkableProps.helptext ? id + '_helptext' : undefined;

	React.useEffect(() => {
		if (!combinedRef.current) return;
		(combinedRef.current as HTMLInputElement).indeterminate = indeterminate;
	}, [combinedRef, indeterminate]);

	return checkableProps.button ? (
		<ButtonCheckable
			ref={combinedRef}
			label={label}
			id={id}
			onChange={handleChange}
			onBlur={handleBlur}
			tooltip={tooltip}
			isInvalid={isInvalid}
			aria-describedby={helptextId}
			{...checkableProps}
		/>
	) : inputonly ? (
		<CheckableInput
			id={id}
			ref={combinedRef}
			onChange={handleChange}
			onBlur={handleBlur}
			isInvalid={isInvalid}
			aria-describedby={helptextId}
			className={className}
			{...checkableProps}
		/>
	) : (
		<div className={formCheckClasses}>
			<CheckableInput
				id={id}
				ref={combinedRef}
				onChange={handleChange}
				onBlur={handleBlur}
				isInvalid={isInvalid}
				aria-describedby={helptextId}
				{...checkableProps}
			/>
			<Label required={!isGroup() && checkableProps.required} htmlFor={id} className={formCheckLabelClasses}>
				{label}
			</Label>
			<Tooltip id={id ? id + '_tooltip' : undefined}>{tooltip}</Tooltip>
			<Helpblock id={helptextId}>{checkableProps.helptext}</Helpblock>
			{!isGroup() && checkableProps.required && label && <FormControlFeedback type="invalid">{label + ' is a required field'}</FormControlFeedback>}
		</div>
	);
}

Checkable.defaultProps = {
	required: false,
	validators: [],
	dirty: undefined,
	button: false
};
